import { FC, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { SharedItemInviteSummary } from "@/pages/utilities/shared-item-invites/shared/types";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { Badge, Flex, Text } from "@radix-ui/themes";
import { Button } from "@/components/button";
import { atlasRoutes } from "@/app/router/routes";
import { useNavigate } from "react-router";
import {
  SHARED_ITEM_KIND_LABELS,
  SHARED_ITEM_INVITE_STATUS_LABELS,
} from "@/pages/utilities/shared-item-invites/shared/constants";

interface SharedItemInviteTableProps {
  invites: SharedItemInviteSummary[];
  pageSize: number;
  onViewInvite: (inviteId: string) => void;
}

export const SharedItemInviteTable: FC<SharedItemInviteTableProps> = ({
  invites,
  pageSize,
  onViewInvite,
}) => {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<SharedItemInviteSummary>[]>(
    () => [
      {
        header: "Invite ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Item Kind",
        accessorFn: row => row.shared_item_kind,
        cell: ({ row }) => (
          <Text size="2">
            {SHARED_ITEM_KIND_LABELS[row.original.shared_item_kind] ??
              row.original.shared_item_kind}
          </Text>
        ),
      },
      {
        header: "Shared Item",
        cell: ({ row }) => <UuidFieldRow id={row.original.shared_item_id} />,
      },
      {
        header: "Inviter Space Account",
        cell: ({ row }) => <UuidFieldRow id={row.original.inviter_space_account_id} />,
      },
      {
        header: "Invitee Email",
        cell: ({ row }) => <Text size="2">{row.original.invitee_email}</Text>,
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <Badge variant="soft">
            {SHARED_ITEM_INVITE_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
        ),
      },
      {
        header: "Revoked",
        cell: ({ row }) => (
          <Badge color={row.original.is_revoked ? "red" : "green"} variant="soft">
            {row.original.is_revoked ? "Yes" : "No"}
          </Badge>
        ),
      },
      {
        header: "Actions",
        cell: ({ row }) => {
          const { id, shared_item_kind, shared_item_id } = row.original;
          const inviteLink = atlasRoutes.sharedItemInvitesView({ params: { inviteId: id } }).path;

          const handleOpenSharedItem = () => {
            if (shared_item_kind === "NOTE") {
              navigate(atlasRoutes.notesView({ params: { noteId: shared_item_id } }).path);
              return;
            }
            if (shared_item_kind === "COLLECTION") {
              navigate(
                atlasRoutes.collectionsView({ params: { collectionId: shared_item_id } }).path
              );
              return;
            }
          };

          return (
            <Flex gap="2" wrap="wrap">
              <Button size="1" onClick={() => onViewInvite(id)}>
                View
              </Button>
              {(shared_item_kind === "NOTE" || shared_item_kind === "COLLECTION") && (
                <Button size="1" variant="soft" onClick={handleOpenSharedItem}>
                  Open Item
                </Button>
              )}
              <Button
                size="1"
                variant="ghost"
                onClick={() => navigator.clipboard.writeText(inviteLink)}
              >
                Copy Link
              </Button>
            </Flex>
          );
        },
      },
    ],
    [navigate, onViewInvite]
  );

  const sortedData = useMemo(
    () => invites.slice().sort((a, b) => b.id.localeCompare(a.id)),
    [invites]
  );

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
